
import { defineComponent, onMounted, reactive } from 'vue'

// @ui-kit
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'

type Period = {
    id: string
    name: string
}

// @new-services
import locationAPI from '@/new-services/location.service'
import tmsAPI from '@/new-services/tms.service'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'

// @composable
import { CarrierT } from '@/core/types/carriers.types'
import { CommentTMS, TransportTypeTMS, TripStatusTMS } from '@/new-services/types/tms'
import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'
import { useDate } from '@/common/composable/useDate'

const PERIODS = [
    { id: 'today', name: 'Сегодня' },
    { id: 'yesterday', name: 'Вчера' },
    { id: 'this_week', name: 'Текущая неделя' },
    { id: 'previous_week', name: 'Прошлая неделя' },
    { id: 'this_month', name: 'Текущий месяц' },
] as Period[]

export default defineComponent({
    components: {
        NewSelect,
        NewButton,
        NewInput,
        NewCheckbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { getToday, getYesterday, getThisWeek, getPreviousWeek, getThisMonth } = useDate()

        const state = reactive({
            tripNumber: null as string | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            companyId: null as number | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            statusIds: [] as number[],
            logistId: null as number | null,
            transportTypeIds: [] as number[],
            commentIds: [] as number[],
            isProject: null as number | null,
            period: null as Period | null,
            isAdditionalLoad: null as number | null,
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            statuses: {
                is_loading: false,
                is_failed: false,
                list: [] as TripStatusTMS[],
            },
            carriers: {
                is_loading: false,
                is_failed: false,
                list: [] as CarrierT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            transport_types: {
                is_loading: false,
                is_failed: false,
                list: [] as TransportTypeTMS[],
            },
            logists: {
                is_loading: false,
                is_failed: false,
                list: [] as UserT[],
                pagination: {
                    current_page: 1,
                    total_pages: 1,
                    limit: null as number | null,
                    total_items: null as number | null,
                },
            },
            comments: {
                is_loading: false,
                is_failed: false,
                list: [] as CommentTMS[],
            },
        })

        const loadComments = async () => {
            try {
                data_state.comments.is_loading = true

                const response = await tmsAPI.getTripComments()
                data_state.comments.list = response.data.data

                data_state.comments.is_failed = false
            } catch (error) {
                data_state.comments.is_failed = true
                console.error(error)
            } finally {
                data_state.comments.is_loading = false
            }
        }

        // Города

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        // Статусы

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await tmsAPI.getStatuses()
                data_state.statuses.list = response.data.data

                data_state.statuses.is_failed = false
            } catch (error) {
                data_state.statuses.is_failed = true
                console.error(error)
            } finally {
                data_state.statuses.is_loading = false
            }
        }

        // Транспортные типы

        const loadTransportTypes = async () => {
            try {
                data_state.transport_types.is_loading = true

                const response = await tmsAPI.getTransportTypes()
                data_state.transport_types.list = response.data.data

                data_state.transport_types.is_failed = false
            } catch (error) {
                data_state.transport_types.is_failed = true
                console.error(error)
            } finally {
                data_state.transport_types.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, search, statusId: 2 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
            } catch (error) {
                data_state.carriers.is_failed = true
                console.error(error)
            } finally {
                data_state.carriers.is_loading = false
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // Логисты

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name)

                data_state.logists.list = logists.data

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page)
                data_state.logists.list = data_state.logists.list.concat(logists.data)

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        //

        const onFiltersChanged = () => {
            if (props.disabled || props.loading) return
            emit('submit', { ...state })
        }

        const onFiltersReset = () => {
            state.tripNumber = null
            state.fromCityId = null
            state.toCityId = null
            state.companyId = null
            state.dateFrom = null
            state.dateTo = null
            state.statusIds = []
            state.logistId = null
            state.transportTypeIds = []
            state.commentIds = []
            state.isProject = null
            state.period = null
            state.isAdditionalLoad = null

            emit('reset')
        }

        const manageToday = () => {
            state.dateFrom = getToday()
            state.dateTo = getToday()
        }

        const manageYesterday = () => {
            state.dateFrom = getYesterday()
            state.dateTo = getYesterday()
        }

        const manageThisWeek = () => {
            const { monday, sunday } = getThisWeek()

            state.dateFrom = monday
            state.dateTo = sunday
        }

        const managePreviousWeek = () => {
            const { previousMonday, previousSunday } = getPreviousWeek()

            state.dateFrom = previousMonday
            state.dateTo = previousSunday
        }

        const manageThisMonth = () => {
            const { firstDay, lastDay } = getThisMonth()

            state.dateFrom = firstDay
            state.dateTo = lastDay
        }

        const onPeriodChange = (period_id: Period['id'] | null) => {
            if (period_id) {
                if (period_id === 'today') {
                    manageToday()
                    return
                }

                if (period_id === 'yesterday') {
                    manageYesterday()
                    return
                }

                if (period_id === 'this_week') {
                    manageThisWeek()
                    return
                }

                if (period_id === 'previous_week') {
                    managePreviousWeek()
                    return
                }

                if (period_id === 'this_month') {
                    manageThisMonth()
                    return
                }
            }

            state.dateFrom = null
            state.dateTo = null
        }

        //

        onMounted(() => {
            loadTransportTypes()
            loadCities()
            loadStatuses()
            loadLogists(1)
            loadCarriers(1)
            loadComments()
        })

        //

        return {
            state,
            data_state,
            //
            onFiltersChanged,
            onFiltersReset,
            //
            loadCities,
            loadStatuses,
            loadTransportTypes,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            //
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            //
            loadComments,
            onPeriodChange,
            PERIODS,
        }
    },
})
